// src/features/kommunikation/components/KommunikationDashboard.tsx

import React, { useState } from 'react';
import {
  Clock, CheckCircle2, XCircle, AlertTriangle, FileText,
  Upload, KeyRound, RefreshCw, Search, X, Loader2, Send
} from 'lucide-react';
import { useDocuments } from '../hooks/useDocuments';
import { DocumentUploadModal } from './DocumentUploadModal';
import { SonderversandModal } from './SonderversandModal';
import { NachversandModal } from './NachversandModal';
import { CredentialsUploadModal } from './CredentialsUploadModal';
import { CredentialsListModal } from './CredentialsListModal';
import { DocumentList } from './DocumentList';

// Deutsche Monatsnamen
const MONATSNAMEN = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

// ============================================
// Wiederverwendbare KPI-Karte (Umsatzanalyse-Stil)
// ============================================
const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  active?: boolean;
  onClick?: () => void;
}> = ({ label, value, icon, color, active, onClick }) => (
  <div
    onClick={onClick}
    style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      border: `1px solid ${active ? color : '#E5E7EB'}`,
      cursor: onClick ? 'pointer' : 'default',
      flex: '1',
      minWidth: '180px',
      transition: 'border-color 0.15s'
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
      <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>{label}</span>
      <div style={{
        width: '36px', height: '36px', borderRadius: '8px',
        backgroundColor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {icon}
      </div>
    </div>
    <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>{value}</div>
  </div>
);

// ============================================
// Einheitlicher Toolbar-Button
// ============================================
const ToolbarButton: React.FC<{
  onClick: () => void;
  color: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ onClick, color, icon, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: '10px 16px',
      backgroundColor: color,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }}
  >
    {icon}
    {children}
  </button>
);

export const KommunikationDashboard: React.FC = () => {
  const {
    documents,
    stats,
    loading,
    error,
    filter,
    updateFilter,
    loadDocuments,
    deleteDocument,
    updateStatus,
    sendDocumentNow,
    sendTestDocument,
    replaceDocument,
    generateProof
  } = useDocuments();

  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showCredentialsListModal, setShowCredentialsListModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSonderversandModal, setShowSonderversandModal] = useState(false);
  // Nachversand an Mitarbeiter (02.11.2026): Sammelpaket-Fälle mit inzwischen
  // vorhandener E-Mail-Adresse direkt nachsenden
  const [showNachversandModal, setShowNachversandModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [mandantSearch, setMandantSearch] = useState<string>(filter.mandantennummer || '');

  // Sonderversand-Ansicht aktiv?
  const isSonderversandView = !!filter.ist_sonderversand;

  /**
   * Gefilterte Dokumente basierend auf Status-Filter (client-side)
   * Monat/Jahr/Mandant werden serverseitig gefiltert
   */
  const filteredDocuments = filterStatus === 'all'
    ? documents
    : documents.filter(doc => doc.versand_status === filterStatus);

  /**
   * Monat ändern
   */
  const handleMonatChange = (monat: number) => {
    setFilterStatus('all');
    updateFilter({ monat });
  };

  /**
   * Jahr ändern
   */
  const handleJahrChange = (jahr: number) => {
    setFilterStatus('all');
    updateFilter({ jahr });
  };

  /**
   * Sonderversand-Ansicht umschalten
   */
  const toggleSonderversandView = () => {
    setFilterStatus('all');
    updateFilter({ ist_sonderversand: isSonderversandView ? undefined : true });
  };

  /**
   * Mandanten-Filter anwenden
   */
  const handleMandantFilter = () => {
    setFilterStatus('all');
    const trimmed = mandantSearch.trim();
    if (trimmed) {
      updateFilter({ mandantennummer: trimmed });
    } else {
      // Mandant-Filter entfernen
      updateFilter({ mandantennummer: undefined });
    }
  };

  /**
   * Mandanten-Filter zurücksetzen
   */
  const handleMandantClear = () => {
    setMandantSearch('');
    setFilterStatus('all');
    updateFilter({ mandantennummer: undefined });
  };

  /**
   * Enter-Taste im Mandant-Suchfeld
   */
  const handleMandantKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleMandantFilter();
    }
  };

  /**
   * Verfügbare Jahre für Dropdown (2025 bis aktuelles Jahr + 1)
   */
  const verfuegbareJahre = (): number[] => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = 2025; y <= currentYear + 1; y++) {
      years.push(y);
    }
    return years;
  };

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>
        <Loader2 size={40} color="#D1D5DB" style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: '16px', fontWeight: '500' }}>Laden...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <div
          style={{
            padding: '16px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: '12px',
            color: '#991B1B',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <AlertTriangle size={18} color="#DC2626" />
          <span>Fehler: {error}</span>
        </div>
      </div>
    );
  }

  // Segmented-Control-Button-Style fuer die Ansicht-Umschaltung
  const segBtn = (selected: boolean, color: string): React.CSSProperties => ({
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'all 0.15s',
    backgroundColor: selected ? 'white' : 'transparent',
    color: selected ? color : '#6B7280',
    boxShadow: selected ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
  });

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 24px 0' }}>
        Kommunikations-Dashboard
      </h1>

      {/* Filter-Bereich */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          padding: '20px',
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          flexWrap: 'wrap',
          alignItems: 'flex-end'
        }}
      >
        {/* Monat */}
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '6px', fontWeight: '500' }}>
            Monat
          </label>
          <select
            value={filter.monat || ''}
            onChange={(e) => handleMonatChange(Number(e.target.value))}
            disabled={isSonderversandView}
            style={{
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: isSonderversandView ? '#F3F4F6' : 'white',
              minWidth: '140px',
              color: '#111827'
            }}
          >
            {MONATSNAMEN.map((name, idx) => (
              <option key={idx + 1} value={idx + 1}>{name}</option>
            ))}
          </select>
        </div>

        {/* Jahr */}
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '6px', fontWeight: '500' }}>
            Jahr
          </label>
          <select
            value={filter.jahr || ''}
            onChange={(e) => handleJahrChange(Number(e.target.value))}
            disabled={isSonderversandView}
            style={{
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: isSonderversandView ? '#F3F4F6' : 'white',
              minWidth: '100px',
              color: '#111827'
            }}
          >
            {verfuegbareJahre().map(j => (
              <option key={j} value={j}>{j}</option>
            ))}
          </select>
        </div>

        {/* Mandant */}
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '6px', fontWeight: '500' }}>
            Mandantennummer
          </label>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input
              type="text"
              value={mandantSearch}
              onChange={(e) => setMandantSearch(e.target.value)}
              onKeyDown={handleMandantKeyDown}
              placeholder="z.B. 42588"
              style={{
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                width: '120px'
              }}
            />
            <button
              onClick={handleMandantFilter}
              style={{
                padding: '8px 12px',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Suchen"
            >
              <Search size={16} />
            </button>
            {filter.mandantennummer && (
              <button
                onClick={handleMandantClear}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Filter zurücksetzen"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Aktiver Mandant-Filter Anzeige */}
        {filter.mandantennummer && (
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#1D4ED8',
            alignSelf: 'flex-end'
          }}>
            Filter: Mandant {filter.mandantennummer}
          </div>
        )}
      </div>

      {/* Stats Overview */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}
      >
        <StatCard
          label="Gesamt"
          value={stats.total}
          icon={<FileText size={18} color="#3B82F6" />}
          color="#3B82F6"
          active={filterStatus === 'all'}
          onClick={() => setFilterStatus('all')}
        />
        <StatCard
          label="Geplant"
          value={stats.geplant}
          icon={<Clock size={18} color="#F59E0B" />}
          color="#F59E0B"
          active={filterStatus === 'geplant'}
          onClick={() => setFilterStatus('geplant')}
        />
        <StatCard
          label="Versendet"
          value={stats.versendet}
          icon={<CheckCircle2 size={18} color="#059669" />}
          color="#059669"
          active={filterStatus === 'versendet'}
          onClick={() => setFilterStatus('versendet')}
        />
        <StatCard
          label="Fehler"
          value={stats.fehler}
          icon={<XCircle size={18} color="#DC2626" />}
          color="#DC2626"
          active={filterStatus === 'fehler'}
          onClick={() => setFilterStatus('fehler')}
        />
        <StatCard
          label="Ohne Credentials"
          value={stats.ohne_credentials}
          icon={<AlertTriangle size={18} color="#F59E0B" />}
          color="#F59E0B"
        />
      </div>

      {/* Actions */}
      <div
        style={{
          marginBottom: '24px',
          padding: '20px',
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '12px'
        }}
      >
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <ToolbarButton onClick={() => setShowUploadModal(true)} color="#3B82F6" icon={<Upload size={16} />}>
            Dokumente hochladen
          </ToolbarButton>

          <ToolbarButton onClick={() => setShowSonderversandModal(true)} color="#8B5CF6" icon={<FileText size={16} />}>
            Sonderversand
          </ToolbarButton>

          <ToolbarButton onClick={() => setShowNachversandModal(true)} color="#0D9488" icon={<Send size={16} />}>
            Nachversand
          </ToolbarButton>

          {/* Ansicht-Umschalter als Segmented Control */}
          <div style={{ display: 'flex', backgroundColor: '#F3F4F6', borderRadius: '8px', padding: '3px' }}>
            <button
              onClick={() => { if (isSonderversandView) toggleSonderversandView(); }}
              style={segBtn(!isSonderversandView, '#3B82F6')}
            >
              Monatsansicht
            </button>
            <button
              onClick={() => { if (!isSonderversandView) toggleSonderversandView(); }}
              style={segBtn(isSonderversandView, '#8B5CF6')}
            >
              Sonderversand
            </button>
          </div>

          <ToolbarButton onClick={() => setShowCredentialsModal(true)} color="#059669" icon={<Upload size={16} />}>
            Credentials importieren
          </ToolbarButton>

          <ToolbarButton onClick={() => setShowCredentialsListModal(true)} color="#0EA5E9" icon={<KeyRound size={16} />}>
            Credentials verwalten
          </ToolbarButton>

          <ToolbarButton onClick={loadDocuments} color="#6B7280" icon={<RefreshCw size={16} />}>
            Aktualisieren
          </ToolbarButton>
        </div>
      </div>

      {/* Aktiver Zeitraum-Hinweis */}
      <div style={{
        marginBottom: '16px',
        fontSize: '14px',
        color: '#6B7280'
      }}>
        {isSonderversandView ? (
          <>Ansicht: <strong>Sonderversand</strong></>
        ) : (
          <>Zeitraum: <strong>{MONATSNAMEN[(filter.monat || 1) - 1]} {filter.jahr}</strong></>
        )}
        {filter.mandantennummer && <> · Mandant: <strong>{filter.mandantennummer}</strong></>}
        {filterStatus !== 'all' && <> · Status: <strong>{filterStatus}</strong></>}
        {' '}({filteredDocuments.length} Dokumente)
      </div>

      {/* Document List */}
      <div
        style={{
          padding: '20px',
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '12px'
        }}
      >
        <DocumentList
          documents={filteredDocuments}
          onDelete={deleteDocument}
          onStatusUpdate={updateStatus}
          onSendNow={sendDocumentNow}
          onTestSend={sendTestDocument}
          onReplace={replaceDocument}
          onGenerateProof={generateProof}
        />
      </div>

      {/* Document Upload Modal */}
      {showUploadModal && (
        <DocumentUploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={loadDocuments}
        />
      )}

      {/* Sonderversand Modal */}
      {showSonderversandModal && (
        <SonderversandModal
          onClose={() => setShowSonderversandModal(false)}
          onSuccess={loadDocuments}
        />
      )}

      {/* Nachversand Modal */}
      {showNachversandModal && (
        <NachversandModal
          onClose={() => setShowNachversandModal(false)}
          onSuccess={loadDocuments}
        />
      )}

      {/* Credentials Upload Modal */}
      <CredentialsUploadModal
        isOpen={showCredentialsModal}
        onClose={() => setShowCredentialsModal(false)}
        onSuccess={loadDocuments}
      />

      {/* Credentials List/Manage Modal */}
      <CredentialsListModal
        isOpen={showCredentialsListModal}
        onClose={() => setShowCredentialsListModal(false)}
      />
    </div>
  );
};
